import {
  DashboardOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { Button, Spin, Tooltip } from "antd";
import type { ReactNode } from "react";
import { Navigate, NavLink, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useStatus } from "./api/queries.js";
import { useAuth } from "./auth/AuthContext.js";
import { timeAgo } from "./format.js";
import { AuditPage } from "./pages/AuditPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { PropertiesPage } from "./pages/PropertiesPage.js";
import { StatusPage } from "./pages/StatusPage.js";

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return <>{children}</>;
}

function StatusStrip() {
  const { data, isError } = useStatus();
  if (isError || !data) {
    return (
      <div className="status-strip">
        <span className={`lamp ${isError ? "off" : ""}`} />
        <span>{isError ? "config server offline" : "connecting…"}</span>
      </div>
    );
  }
  return (
    <div className="status-strip">
      <span className="lamp" />
      <span>
        update <b>#{data.updateId}</b>
      </span>
      <span>
        snapshot <b>{data.lastSnapshotAt ? timeAgo(data.lastSnapshotAt) : "—"}</b>
      </span>
      <span>
        subs <b>{data.activeSubscriptions}</b>
      </span>
    </div>
  );
}

const PAGE_TITLES: Record<string, string> = {
  "/properties": "Properties",
  "/audit": "Audit log",
  "/status": "Status",
};

function Shell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] ?? "";
  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail-brand">a/c</div>
        <NavLink to="/properties" className={({ isActive }) => `rail-item ${isActive ? "active" : ""}`}>
          <DatabaseOutlined />
          <span className="rail-label">props</span>
        </NavLink>
        <NavLink to="/audit" className={({ isActive }) => `rail-item ${isActive ? "active" : ""}`}>
          <HistoryOutlined />
          <span className="rail-label">audit</span>
        </NavLink>
        <NavLink to="/status" className={({ isActive }) => `rail-item ${isActive ? "active" : ""}`}>
          <DashboardOutlined />
          <span className="rail-label">status</span>
        </NavLink>
        <div className="rail-spacer" />
        <Tooltip title={`sign out ${user?.username ?? ""}`} placement="right">
          <Button type="text" icon={<LogoutOutlined />} onClick={() => void logout()} />
        </Tooltip>
      </aside>
      <div className="shell-main">
        <header className="topbar">
          <div className="topbar-title">{title}</div>
          <div className="topbar-spacer" />
          <StatusStrip />
          <span className="mono dim" style={{ fontSize: 11 }}>
            {user?.username}
          </span>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/properties" replace />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/status" element={<StatusPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
